// XLSX is dynamically imported in exportToExcel to reduce initial bundle size
import { saveAs } from 'file-saver';
import { logger } from './logger';

export interface ExportOptions {
  filename?: string;
  includeHeaders?: boolean;
  format?: 'csv' | 'xlsx';
  dateFormat?: string;
}

export interface ExportData {
  headers: string[];
  rows: (string | number | boolean | null | undefined)[][];
  metadata?: {
    title?: string;
    exportedAt?: string;
    filters?: string[];
    sortOrder?: string[];
  };
}

/**
 * Export data to CSV format
 * 
 * Includes input validation for consistency and safety.
 */
export function exportToCSV(data: ExportData, options: ExportOptions = {}): void {
  try {
    const { filename = 'export.csv', includeHeaders = true } = options;
    
    // Input validation
    if (!data || typeof data !== 'object') {
      throw new Error('Export data must be an object');
    }
    
    if (!Array.isArray(data.headers)) {
      throw new Error('Export data headers must be an array');
    }
    
    if (!Array.isArray(data.rows)) {
      throw new Error('Export data rows must be an array');
    }
    
    // Reasonable size limits
    const MAX_ROWS = 10000;
    if (data.rows.length > MAX_ROWS) {
      throw new Error(`Export data too large: ${data.rows.length} rows exceeds maximum of ${MAX_ROWS}`);
    }
    
    // Validate filename
    if (typeof filename !== 'string' || filename.trim().length === 0) {
      throw new Error('Export filename must be a non-empty string');
    }
    
    logger.info('Starting CSV export', { 
      filename, 
      rowCount: data.rows.length,
      columnCount: data.headers.length 
    }, 'Export');

    let csvContent = '';

    // Add headers if requested
    if (includeHeaders) {
      csvContent += data.headers.join(',') + '\n';
    }

    // Add data rows
    data.rows.forEach(row => {
      const escapedRow = row.map(cell => {
        const cellStr = String(cell || '');
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      });
      csvContent += escapedRow.join(',') + '\n';
    });

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);

    logger.info('CSV export completed successfully', { filename }, 'Export');
  } catch (error) {
    logger.error('CSV export failed', { error, filename: options.filename }, 'Export');
    throw new Error(`Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Export data to Excel format
 * 
 * Includes input validation as defense-in-depth mitigation for xlsx vulnerability.
 * Validates data structure and size before processing.
 * 
 * XLSX is dynamically imported to reduce initial bundle size (~500KB savings).
 */
export async function exportToExcel(data: ExportData, options: ExportOptions = {}): Promise<void> {
  try {
    const { filename = 'export.xlsx', includeHeaders = true } = options;
    
    // Input validation - defense-in-depth for xlsx vulnerability mitigation
    if (!data || typeof data !== 'object') {
      throw new Error('Export data must be an object');
    }
    
    if (!Array.isArray(data.headers)) {
      throw new Error('Export data headers must be an array');
    }
    
    if (!Array.isArray(data.rows)) {
      throw new Error('Export data rows must be an array');
    }
    
    // Reasonable size limits to prevent memory exhaustion
    const MAX_ROWS = 10000;
    const MAX_COLUMNS = 100;
    
    if (data.rows.length > MAX_ROWS) {
      throw new Error(`Export data too large: ${data.rows.length} rows exceeds maximum of ${MAX_ROWS}`);
    }
    
    if (data.headers.length > MAX_COLUMNS) {
      throw new Error(`Export data too wide: ${data.headers.length} columns exceeds maximum of ${MAX_COLUMNS}`);
    }
    
    // Validate filename is a string and not empty
    if (typeof filename !== 'string' || filename.trim().length === 0) {
      throw new Error('Export filename must be a non-empty string');
    }
    
    logger.info('Starting Excel export', { 
      filename, 
      rowCount: data.rows.length,
      columnCount: data.headers.length 
    }, 'Export');

    // Dynamic import of XLSX to reduce initial bundle size
    const XLSX = await import('xlsx');

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // Add headers if requested
    if (includeHeaders) {
      XLSX.utils.sheet_add_aoa(worksheet, [data.headers], { origin: 'A1' });
    }

    // Add data rows
    if (data.rows.length > 0) {
      const startRow = includeHeaders ? 1 : 0;
      XLSX.utils.sheet_add_aoa(worksheet, data.rows, { origin: `A${startRow + 1}` });
    }

    // Add metadata if provided
    if (data.metadata) {
      const metadataRow = data.rows.length + (includeHeaders ? 2 : 1);
      
      if (data.metadata.title) {
        XLSX.utils.sheet_add_aoa(worksheet, [['Title', data.metadata.title]], { origin: `A${metadataRow}` });
      }
      
      if (data.metadata.exportedAt) {
        XLSX.utils.sheet_add_aoa(worksheet, [['Exported At', data.metadata.exportedAt]], { origin: `A${metadataRow + 1}` });
      }
      
      if (data.metadata.filters && data.metadata.filters.length > 0) {
        XLSX.utils.sheet_add_aoa(worksheet, [['Filters', data.metadata.filters.join(', ')]], { origin: `A${metadataRow + 2}` });
      }
      
      if (data.metadata.sortOrder && data.metadata.sortOrder.length > 0) {
        XLSX.utils.sheet_add_aoa(worksheet, [['Sort Order', data.metadata.sortOrder.join(', ')]], { origin: `A${metadataRow + 3}` });
      }
    }

    // Set column widths
    const colWidths = data.headers.map((header, index) => {
      const maxLength = Math.max(
        header.length,
        ...data.rows.map(row => String(row[index] || '').length)
      );
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });
    worksheet['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

    // Generate and download file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, filename);

    logger.info('Excel export completed successfully', { filename }, 'Export');
  } catch (error) {
    logger.error('Excel export failed', { error, filename: options.filename }, 'Export');
    throw new Error(`Failed to export Excel: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Export data in both CSV and Excel formats simultaneously
 * 
 * Creates two files with the same base filename but different extensions.
 * Timestamp is added to both filenames for uniqueness.
 * 
 * @param data ExportData object with headers, rows, and optional metadata
 * @param baseFilename Base filename without extension (e.g., 'campaign-spend')
 * @param options Export options (filename, includeHeaders, etc.)
 * 
 * @example
 * exportToBoth(exportData, 'campaign-spend');
 * // Creates: campaign-spend_2025-12-05T12-30-45.csv
 * //          campaign-spend_2025-12-05T12-30-45.xlsx
 */
export async function exportToBoth(data: ExportData, baseFilename: string, options: ExportOptions = {}): Promise<void> {
  try {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const baseName = baseFilename.replace(/\.[^/.]+$/, ''); // Remove extension if present

    // Export CSV
    exportToCSV(data, {
      ...options,
      filename: `${baseName}_${timestamp}.csv`
    });

    // Export Excel (now async)
    await exportToExcel(data, {
      ...options,
      filename: `${baseName}_${timestamp}.xlsx`
    });

    logger.info('Both CSV and Excel exports completed successfully', { baseFilename }, 'Export');
  } catch (error) {
    logger.error('Dual export failed', { error, baseFilename }, 'Export');
    throw error;
  }
}

/**
 * Format table data for export (CSV/Excel)
 * 
 * Converts table data with columns into ExportData format.
 * Handles different data types (Date, number, string) and formats them appropriately.
 * 
 * @param data Array of table row data
 * @param columns Array of column definitions with key and label
 * @param getCellValue Optional function to extract cell value (defaults to direct property access)
 * @returns ExportData object with headers, rows, and metadata
 * 
 * @example
 * const exportData = formatTableDataForExport(
 *   [{ name: 'John', age: 30, date: new Date('2025-01-01') }],
 *   [{ key: 'name', label: 'Name' }, { key: 'age', label: 'Age' }]
 * );
 */
export function formatTableDataForExport<T>(
  data: T[],
  columns: Array<{ key: string; label: string }>,
  getCellValue?: (row: T, key: string) => any
): ExportData {
  const headers = columns.map(col => col.label);
  const rows = data.map(row => 
    columns.map(col => {
      const value = getCellValue 
        ? getCellValue(row, col.key) 
        : (typeof row === 'object' && row !== null && col.key in row 
          ? (row as Record<string, unknown>)[col.key] 
          : undefined);
      
      // Format different data types
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      if (typeof value === 'number') {
        return value.toLocaleString();
      }
      if (value === null || value === undefined) {
        return '';
      }
      
      return String(value);
    })
  );

  return {
    headers,
    rows,
    metadata: {
      exportedAt: new Date().toLocaleString()
    }
  };
}

